import React, { useCallback, useState } from 'react';

import {
  useGetList,
  useRecordContext,
  ArrayField,
} from 'react-admin';

import {
  Box,
  Button,
  Dialog,
  Divider,
  CardHeader,
  Card,
  DialogActions,
  IconButton,
} from '@mui/material';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import LayersClearIcon from '@mui/icons-material/LayersClear';

import { get, isUndefined, size } from 'lodash';
import useDialogStatus from '../../hooks/useDialogStatus';

import RuleDatagrid from '../rules/components/RuleDatagrid';
import SelectInputButton from '../layout/inputFields/SelectInputButton';
import Pagination from '../layout/Pagination';
import { RuleVersion } from '../dashboard/components/RuleTable';

import { Rule } from '../../utilities/schemas/rules';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import ruleTypes from '../../constants/ruleTypes';

type CustomButtonProps = {
  onChange: (values: Set<string>) => void;
  values: Set<string>;
  field: string;
};

const CustomButton = ({
  onChange,
  values,
  field,
}: CustomButtonProps) => {
  const rule = useRecordContext<Rule>();
  const ruleField: string | undefined = get(rule, field);

  const onDelete = useCallback(() => {
    onChange(new Set([...values].filter((item) => item !== ruleField)));
  }, [onChange, ruleField, values]);

  const onAdd = useCallback(() => {
    if (!ruleField) return;
    onChange(new Set(values.add(ruleField)));
  }, [onChange, ruleField, values]);

  if (isUndefined(ruleField)) return null;

  const contains = values.has(ruleField);

  if (contains) {
    return (
      <IconButton
        onClick={onDelete}
        color="error"
      >
        <RemoveCircleOutlineIcon />
      </IconButton>
    );
  }
  return (
    <IconButton
      onClick={onAdd}
      color="secondary"
    >
      <AddCircleOutlineIcon />
    </IconButton>
  );
};

type SelectRuleProps = {
  onChange: (values: Set<string>) => void;
  onSubmit?: () => void;
  onClose?: () => void;
  values: Set<string>;
  field: 'id' | 'originationChainId' | 'name';
  label: string;
};

const SelectRule = ({
  label,
  field,
  onChange,
  onSubmit,
  onClose,
  values,
} : SelectRuleProps) => {
  const [ruleType, setRuleType] = useState(ruleTypes.REQUEST);
  const [version, setVersion] = useState('');

  const { data } = useGetList('rules', {
    filter: { ruleType, version },
    pagination: { page: 1, perPage: 500 },
  });

  const { open, openDialog, closeDialog } = useDialogStatus();

  const handleSave = useCallback(() => {
    if (onSubmit) onSubmit();
    closeDialog();
  }, [onSubmit, closeDialog]);

  const handleClose = useCallback(() => {
    if (onClose) onClose();
    closeDialog();
  }, [onClose, closeDialog]);

  const handleSelectAll = useCallback(() => {
    onChange(new Set(data?.map((rule) => get(rule, field))));
  }, [onChange, data, field]);

  const handleUnselectAll = useCallback(() => {
    onChange(new Set());
  }, [onChange]);

  return (
    <>
      <Button fullWidth startIcon={<PlaylistAddIcon />} variant="contained" onClick={openDialog}>{`${label} (${values.size})`}</Button>
      <Dialog scroll="body" transitionDuration={500} open={open} fullWidth maxWidth="xl">
        <Card style={{ margin: 0 }}>
          <CardHeader
            title={`${label}`}
            action={(
              <Box display="flex" gap={2}>
                {values.size !== size(data) && (
                  <Button
                    startIcon={<LibraryAddIcon />}
                    onClick={handleSelectAll}
                  >
                    Select all
                  </Button>
                )}
                {values.size === size(data) && (
                  <Button
                    startIcon={<LayersClearIcon />}
                    onClick={handleUnselectAll}
                  >
                    Unselect all
                  </Button>
                )}
                <SelectInputButton
                  choices={[
                    { id: 'request', name: 'Request rule' },
                    { id: 'ongoing', name: 'Ongoing rule' },
                  ]}
                  value={ruleType}
                  setValue={setRuleType}
                />
                <RuleVersion
                  value={version}
                  setValue={setVersion}
                  ruleType={ruleType}
                />
              </Box>
            )}

          />
          <Divider />
          {data && (
            <ArrayField source="data" record={{ data }} perPage={15}>
              <RuleDatagrid
                sx={boldDataGridStyle}
                ruleType={ruleType}
                idFieldSource={field}
                idFieldLabel={field === 'originationChainId' ? 'Origination rule id' : undefined}
              >
                <CustomButton
                  onChange={onChange}
                  values={values}
                  field={field}
                />
              </RuleDatagrid>
              <Pagination disableEmptyText />
            </ArrayField>
          )}
        </Card>
        <DialogActions>
          <Button onClick={handleClose} startIcon={<CloseIcon />} color="error" variant="outlined">Close</Button>
          {!!onSubmit && (
            <Button onClick={handleSave} startIcon={<SaveIcon />} variant="outlined">Save</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectRule;
